import { createTheme, alpha } from '@mui/material/styles';

// ─────────────────────────────────────────────────────────────────────────────
// Palette "shadcn / zinc dark" — surfaces neutres, bordures fines, accent indigo
// ─────────────────────────────────────────────────────────────────────────────
const tokens = {
  background: '#09090b', // zinc-950 (page)
  card: '#101012',       // surface légèrement surélevée (cards / sidebar)
  cardHover: '#161618',
  popover: '#0c0c0e',
  border: '#27272a',     // zinc-800
  borderStrong: '#3f3f46',
  input: '#18181b',      // zinc-900
  foreground: '#fafafa', // zinc-50
  muted: '#a1a1aa',      // zinc-400
  mutedFaint: '#71717a', // zinc-500
  primary: '#6366f1',    // indigo-500 (accent de marque)
  primaryHover: '#4f46e5',
  ring: '#6366f1',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#38bdf8',
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.primary,
      dark: tokens.primaryHover,
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#a78bfa', // violet-400
      contrastText: '#0b0b0f',
    },
    success: { main: tokens.success },
    warning: { main: tokens.warning },
    error: { main: tokens.error },
    info: { main: tokens.info },
    background: {
      default: tokens.background,
      paper: tokens.card,
    },
    divider: tokens.border,
    text: {
      primary: tokens.foreground,
      secondary: tokens.muted,
      disabled: tokens.mutedFaint,
    },
    action: {
      hover: alpha('#ffffff', 0.04),
      selected: alpha('#ffffff', 0.08),
      active: tokens.muted,
    },
  },

  shape: { borderRadius: 10 },

  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 600, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: tokens.muted },
    body2: { color: tokens.muted },
    button: { fontWeight: 500, letterSpacing: 0 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': { colorScheme: 'dark' },
        body: {
          backgroundColor: tokens.background,
          color: tokens.foreground,
        },
        // Scrollbar fine et discrète (style shadcn)
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: tokens.borderStrong,
          borderRadius: 8,
          border: `2px solid ${tokens.background}`,
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#52525b' },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: tokens.card,
          border: `1px solid ${tokens.border}`,
        },
        rounded: { borderRadius: 14 },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: tokens.card,
          border: `1px solid ${tokens.border}`,
          borderRadius: 14,
          backgroundImage: 'none',
          transition: 'border-color .2s ease, background-color .2s ease',
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: { fontSize: '1rem', fontWeight: 600 },
        subheader: { color: tokens.muted, fontSize: '0.8125rem' },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          paddingInline: 14,
          paddingBlock: 7,
          boxShadow: 'none',
          transition: 'background-color .15s ease, border-color .15s ease, color .15s ease',
        },
        containedPrimary: {
          backgroundColor: tokens.primary,
          '&:hover': { backgroundColor: tokens.primaryHover, boxShadow: 'none' },
        },
        outlined: {
          borderColor: tokens.border,
          color: tokens.foreground,
          backgroundColor: 'transparent',
          '&:hover': {
            borderColor: tokens.borderStrong,
            backgroundColor: alpha('#ffffff', 0.04),
          },
        },
        text: {
          color: tokens.foreground,
          '&:hover': { backgroundColor: alpha('#ffffff', 0.04) },
        },
        sizeSmall: { paddingInline: 10, paddingBlock: 4, fontSize: '0.8125rem' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          color: tokens.muted,
          '&:hover': { backgroundColor: alpha('#ffffff', 0.05), color: tokens.foreground },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.input,
        },
        outlined: { backgroundColor: 'transparent' },
        filledSuccess: { backgroundColor: alpha(tokens.success, 0.15), color: '#86efac', border: `1px solid ${alpha(tokens.success, 0.3)}` },
        filledError: { backgroundColor: alpha(tokens.error, 0.15), color: '#fca5a5', border: `1px solid ${alpha(tokens.error, 0.3)}` },
        filledWarning: { backgroundColor: alpha(tokens.warning, 0.15), color: '#fcd34d', border: `1px solid ${alpha(tokens.warning, 0.3)}` },
        filledPrimary: { backgroundColor: alpha(tokens.primary, 0.18), color: '#c7d2fe', border: `1px solid ${alpha(tokens.primary, 0.35)}` },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.input,
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.borderStrong },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ring,
            borderWidth: 1,
            boxShadow: `0 0 0 3px ${alpha(tokens.ring, 0.25)}`,
          },
        },
        input: { paddingBlock: 11 },
      },
    },
    MuiInputLabel: { styleOverrides: { root: { color: tokens.muted } } },
    MuiFormHelperText: { styleOverrides: { root: { color: tokens.mutedFaint } } },

    MuiSelect: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.popover,
          borderRadius: 10,
          boxShadow: '0 10px 30px -10px rgba(0,0,0,.6)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 4,
          '&:hover': { backgroundColor: alpha('#ffffff', 0.06) },
          '&.Mui-selected': { backgroundColor: alpha(tokens.primary, 0.18) },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#18181b',
          border: `1px solid ${tokens.border}`,
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 8,
          padding: '6px 10px',
        },
        arrow: { color: '#18181b' },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.border },
        head: {
          color: tokens.muted,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          backgroundColor: tokens.background,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: alpha('#ffffff', 0.025) } },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: tokens.border } } },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 40,
          color: tokens.muted,
          '&.Mui-selected': { color: tokens.foreground },
        },
      },
    },
    MuiTabs: {
      styleOverrides: { indicator: { backgroundColor: tokens.primary, height: 2, borderRadius: 2 } },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, border: `1px solid ${tokens.border}`, alignItems: 'center' },
        standardError: { backgroundColor: alpha(tokens.error, 0.12), color: '#fca5a5' },
        standardWarning: { backgroundColor: alpha(tokens.warning, 0.12), color: '#fcd34d' },
        standardSuccess: { backgroundColor: alpha(tokens.success, 0.12), color: '#86efac' },
        standardInfo: { backgroundColor: alpha(tokens.info, 0.12), color: '#7dd3fc' },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, border: `1px solid ${tokens.border}`, backgroundImage: 'none' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 999, backgroundColor: tokens.input },
        bar: { borderRadius: 999 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        track: { borderRadius: 999, backgroundColor: tokens.borderStrong, opacity: 1 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(tokens.background, 0.8),
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${tokens.border}`,
          boxShadow: 'none',
          backgroundImage: 'none',
        },
      },
    },
  },
});

export const designTokens = tokens;
export default theme;
